import React, { createContext, useContext, useState, useEffect, useRef } from 'react'
import {
    User as FirebaseUser,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    sendEmailVerification,
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { User } from '../../../shared/types/user.types'

const API_URL = import.meta.env.VITE_API_URL as string
import { IAddress } from '../../../shared/types/garage.types'
import useGarage from '../wrappers/use-garage'
import useUser from '../wrappers/use-user'

interface IGoogleSignInResult {
    uid: string
    displayName: string | null
    email: string | null
    isNewUser: boolean
}

interface IAuthContext {
    user: User | null
    isAuthenticated: boolean
    emailVerified: boolean
    loading: boolean
    login: (email: string, password: string) => Promise<void>
    loginWithGoogle: () => Promise<void>
    register: (email: string, password: string, firstName: string, lastName: string, address: IAddress, marketingConsent?: boolean) => Promise<void>
    logout: () => Promise<void>
    initiateGoogleSignIn: () => Promise<IGoogleSignInResult>
    completeRegistration: (uid: string, firstName: string, lastName: string, email: string, address: IAddress, marketingConsent?: boolean) => Promise<void>
    updateUserProfile: (data: { firstName?: string; lastName?: string; phone?: string }) => Promise<void>
    updateMarketingConsent: (value: boolean) => Promise<void>
    registerWithToken: (token: string, email: string, password: string, firstName: string, lastName: string, marketingConsent?: boolean) => Promise<void>
    registerWithTokenViaGoogle: (token: string, firstName: string, lastName: string, marketingConsent?: boolean) => Promise<void>
    sendVerificationEmail: () => Promise<void>
    reloadUser: () => Promise<void>
    clearStatus: () => Promise<void>
}

const AuthContext = createContext<IAuthContext | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext)
    if (!context) throw new Error('useAuth must be used within AuthProvider')
    return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
    const [appUser, setAppUser] = useState<User | null>(null)
    const [emailVerified, setEmailVerified] = useState(false)
    const [loading, setLoading] = useState(true)
    const { create: createGarage } = useGarage()
    const { get: getUser, set: setUser, create: createUser, remove: removeUser, findByToken } = useUser()

    const getUserRef = useRef(getUser)
    useEffect(() => { getUserRef.current = getUser }, [getUser])

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            setFirebaseUser(fbUser)
            setEmailVerified(fbUser?.emailVerified ?? false)
            try {
                if (fbUser) {
                    const userFromDb = await getUserRef.current(fbUser.uid)
                    if (userFromDb) {
                        // Auto-sync custom claims if role doesn't match token claims
                        const idTokenResult = await fbUser.getIdTokenResult()
                        const claimRole = idTokenResult.claims.role as string | undefined
                        if (claimRole !== userFromDb.role) {
                            try {
                                const token = await fbUser.getIdToken()
                                await fetch(`${API_URL}/api/users/me/sync-claims`, {
                                    method: 'POST',
                                    headers: { Authorization: `Bearer ${token}` },
                                })
                                await fbUser.getIdToken(true) // force-refresh token with new claims
                            } catch {
                                // non-critical — ignore sync errors
                            }
                        }

                        const firstName = userFromDb.firstName || ''
                        const lastName = userFromDb.lastName || ''
                        setAppUser({
                            uid: fbUser.uid,
                            name: userFromDb.name || `${firstName} ${lastName}`.trim() || fbUser.displayName || fbUser.email?.split('@')[0] || '',
                            firstName,
                            lastName,
                            email: fbUser.email || '',
                            role: userFromDb.role || 'customer',
                            garageUid: userFromDb.garageUid,
                            dealerId: userFromDb.dealerId,
                            phone: userFromDb.phone,
                            marketingConsent: userFromDb.marketingConsent,
                        })
                    } else {
                        // Firestore doc ještě neexistuje (probíhá registrace přes Google)
                        const displayName = fbUser.displayName || ''
                        const parts = displayName.split(' ')
                        setAppUser({
                            uid: fbUser.uid,
                            name: displayName || fbUser.email?.split('@')[0] || '',
                            firstName: parts[0] || '',
                            lastName: parts.slice(1).join(' '),
                            email: fbUser.email || '',
                            role: 'customer',
                        })
                    }
                } else {
                    setAppUser(null)
                }
            } catch (error) {
                console.error('Failed to load user profile:', error)
                setAppUser(null)
            } finally {
                setLoading(false)
            }
        })
        return unsubscribe
    }, [])

    /**
     * Přihlášení e-mailem a heslem.
     */
    const login = async (email: string, password: string) => {
        await signInWithEmailAndPassword(auth, email, password)
    }

    /**
     * Přihlášení přes Google (pro existující uživatele z Login stránky).
     * Pokud uživatel ještě nemá Firestore dokument, vytvoří ho.
     */
    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider()
        const result = await signInWithPopup(auth, provider)
        const existingUser = await getUser(result.user.uid)
        if (!existingUser) {
            const displayName = result.user.displayName || ''
            const parts = displayName.split(' ')
            await createUser(result.user.uid, {
                firstName: parts[0] || '',
                lastName: parts.slice(1).join(' '),
                name: displayName || result.user.email?.split('@')[0] || '',
                email: result.user.email,
                role: 'customer',
                status: '',
            })
        }
    }

    /**
     * Registrace e-mailem — vytvoří Firebase Auth uživatele, Firestore dokument a garáž.
     */
    const register = async (email: string, password: string, firstName: string, lastName: string, address: IAddress, marketingConsent = false) => {
        const result = await createUserWithEmailAndPassword(auth, email, password)
        await sendEmailVerification(result.user)
        const uid = result.user.uid
        const name = `${firstName} ${lastName}`.trim()

        try {
            const garage = await createGarage({
                address: {
                    street: address.street,
                    houseNumber: address.houseNumber,
                    city: address.city,
                    zip: address.zip,
                },
            })

            await createUser(uid, {
                firstName,
                lastName,
                name,
                email,
                role: 'customer',
                status: '',
                garageUid: garage.uid,
                marketingConsent,
                updatedAt: new Date(),
            })
        } catch (error) {
            await result.user.delete()
            throw error
        }
    }

    /**
     * Spustí Google OAuth popup a vrátí základní data uživatele.
     * isNewUser je true pokud Firestore dokument pro tohoto uživatele ještě neexistuje.
     * Firestore dokument se NEVYTVÁŘÍ — to obstarává completeRegistration().
     */
    const initiateGoogleSignIn = async (): Promise<IGoogleSignInResult> => {
        const provider = new GoogleAuthProvider()
        const result = await signInWithPopup(auth, provider)
        const existingUser = await getUser(result.user.uid)
        return {
            uid: result.user.uid,
            displayName: result.user.displayName,
            email: result.user.email,
            isNewUser: !existingUser,
        }
    }

    /**
     * Dokončí registraci Google uživatele — zapíše Firestore dokument,
     * vytvoří garáž a aktualizuje stav AuthContext.
     */
    const completeRegistration = async (uid: string, firstName: string, lastName: string, email: string, address: IAddress, marketingConsent = false) => {
        const name = `${firstName} ${lastName}`.trim()

        try {
            const garage = await createGarage({
                address: {
                    street: address.street,
                    houseNumber: address.houseNumber,
                    city: address.city,
                    zip: address.zip,
                },
            })

            await createUser(uid, {
                firstName,
                lastName,
                name,
                email,
                role: 'customer',
                status: '',
                garageUid: garage.uid,
                marketingConsent,
                updatedAt: new Date(),
            })
            setAppUser({
                uid,
                name,
                firstName,
                lastName,
                email,
                role: 'customer',
                garageUid: garage.uid,
            })
        } catch (error) {
            if (auth.currentUser && auth.currentUser.uid === uid) await auth.currentUser.delete()
            throw error
        }
    }

    /**
     * Aktualizuje osobní údaje uživatele přes API (validace na serveru).
     */
    const updateUserProfile = async (data: { firstName?: string; lastName?: string; phone?: string }) => {
        if (!firebaseUser) throw new Error('Not authenticated')
        const token = await firebaseUser.getIdToken()

        const body: Record<string, unknown> = {}
        if (data.firstName !== undefined) body.firstName = data.firstName
        if (data.lastName !== undefined) body.lastName = data.lastName
        if (data.phone !== undefined) body.phone = data.phone

        const res = await fetch(`${API_URL}/api/users/me`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body),
        })

        if (!res.ok) {
            const err = await res.json().catch(() => ({})) as { error?: string }
            throw new Error(err.error || 'Profile update failed')
        }

        const newFirst = data.firstName ?? appUser?.firstName ?? ''
        const newLast = data.lastName ?? appUser?.lastName ?? ''
        const newName = data.firstName !== undefined || data.lastName !== undefined
            ? `${newFirst} ${newLast}`.trim()
            : undefined

        setAppUser((prev: User | null) => prev ? {
            ...prev,
            firstName: data.firstName ?? prev.firstName,
            lastName: data.lastName ?? prev.lastName,
            phone: data.phone ?? prev.phone,
            ...(newName !== undefined && { name: newName }),
        } : null)
    }

    /**
     * Aktualizuje souhlas s marketingovou komunikací.
     */
    const updateMarketingConsent = async (value: boolean) => {
        if (!firebaseUser) throw new Error('Not authenticated')
        await setUser(firebaseUser.uid, { marketingConsent: value, updatedAt: new Date() })
        setAppUser((prev: User | null) => prev ? { ...prev, marketingConsent: value } : null)
    }

    /**
     * Registrace přes odkaz z pozvánky — najde pending user doc dle tokenu,
     * vytvoří Firebase Auth účet a aktivuje uživatele s rolí z pozvánky.
     */
    const registerWithToken = async (token: string, email: string, password: string, firstName: string, lastName: string, marketingConsent = false) => {
        const invite = await findByToken(token)

        if (!invite) throw new Error('Pozvánka nebyla nalezena nebo vypršela.')

        if (invite.status !== 'pending') throw new Error('Pozvánka již byla použita nebo je neplatná.')

        if (invite.expiresAt && new Date(invite.expiresAt) < new Date()) {
            throw new Error('Platnost pozvánky vypršela.')
        }

        const role = invite.role
        const name = `${firstName} ${lastName}`.trim()

        const result = await createUserWithEmailAndPassword(auth, email, password)
        await sendEmailVerification(result.user)
        const uid = result.user.uid

        let userDocCreated = false
        try {
            let garageUid: string | undefined
            if (role === 'customer') {
                const garage = await createGarage({
                    address: { street: '', houseNumber: '', city: '', zip: '' },
                })
                garageUid = garage.uid
            }

            await createUser(uid, {
                firstName,
                lastName,
                name,
                email,
                role,
                status: '',
                ...(garageUid && { garageUid }),
                ...(invite.dealerId && { dealerId: invite.dealerId }),
                ...((role === 'customer') && { marketingConsent }),
            })
            userDocCreated = true

            await removeUser(invite.uid)
        } catch (error) {
            await result.user.delete().catch(() => {})
            if (userDocCreated) await removeUser(uid).catch(() => {})
            throw error
        }
    }

    /**
     * Registrace přes Google na základě pozvánky — ověří shodu e-mailu,
     * vytvoří Firestore user doc s rolí z pozvánky a smaže pending záznam.
     */
    const registerWithTokenViaGoogle = async (token: string, firstName: string, lastName: string, marketingConsent = false) => {
        const invite = await findByToken(token)
        if (!invite) throw new Error('Pozvánka nebyla nalezena nebo vypršela.')
        if (invite.status !== 'pending') throw new Error('Pozvánka již byla použita nebo je neplatná.')
        if (invite.expiresAt && new Date(invite.expiresAt) < new Date()) {
            throw new Error('Platnost pozvánky vypršela.')
        }

        const role = invite.role
        const name = `${firstName} ${lastName}`.trim()

        const provider = new GoogleAuthProvider()
        const result = await signInWithPopup(auth, provider)

        if (result.user.email?.toLowerCase() !== invite.email?.toLowerCase()) {
            await signOut(auth)
            throw new Error(`Google účet musí mít e-mail shodný s pozvánkou (${invite.email}).`)
        }

        const uid = result.user.uid
        let userDocCreated = false
        try {
            let garageUid: string | undefined
            if (role === 'customer') {
                const garage = await createGarage({
                    address: { street: '', houseNumber: '', city: '', zip: '' },
                })
                garageUid = garage.uid
            }

            await createUser(uid, {
                firstName,
                lastName,
                name,
                email: result.user.email!,
                role,
                status: '',
                ...(garageUid && { garageUid }),
                ...(invite.dealerId && { dealerId: invite.dealerId }),
                ...((role === 'customer') && { marketingConsent }),
            })
            userDocCreated = true

            await removeUser(invite.uid)

            // Aktualizuj appUser — onAuthStateChanged proběhl dříve než byl doc vytvořen
            // (race condition), takže garageUid/dealerId v appUser chybí
            setAppUser({
                uid,
                name,
                firstName,
                lastName,
                email: result.user.email!,
                role,
                ...(garageUid && { garageUid }),
                ...(invite.dealerId && { dealerId: invite.dealerId }),
            })
        } catch (error) {
            await result.user.delete().catch(() => {})
            if (userDocCreated) await removeUser(uid).catch(() => {})
            throw error
        }
    }

    /**
     * Odešle ověřovací e-mail aktuálně přihlášenému uživateli.
     */
    const sendVerificationEmail = async () => {
        if (!auth.currentUser) throw new Error('Not authenticated')
        await sendEmailVerification(auth.currentUser)
    }

    /**
     * Znovu načte stav Firebase uživatele (emailVerified apod.) a zároveň
     * obnoví appUser z Firestore — řeší race condition kdy onAuthStateChanged
     * proběhl před vytvořením Firestore dokumentu (garageUid chybí).
     */
    const reloadUser = async () => {
        if (!auth.currentUser) return
        await auth.currentUser.reload()
        setEmailVerified(auth.currentUser.emailVerified)
        setFirebaseUser(auth.currentUser)
        try {
            const userFromDb = await getUser(auth.currentUser.uid)
            if (userFromDb) {
                const firstName = userFromDb.firstName || ''
                const lastName = userFromDb.lastName || ''
                setAppUser({
                    uid: auth.currentUser.uid,
                    name: userFromDb.name || `${firstName} ${lastName}`.trim() || auth.currentUser.displayName || auth.currentUser.email?.split('@')[0] || '',
                    firstName,
                    lastName,
                    email: auth.currentUser.email || '',
                    role: userFromDb.role || 'customer',
                    garageUid: userFromDb.garageUid,
                    dealerId: userFromDb.dealerId,
                    phone: userFromDb.phone,
                    marketingConsent: userFromDb.marketingConsent,
                })
            }
        } catch {
            // non-critical
        }
    }

    /**
     * Zruší vlastní stav účtu (např. requestDelete) — odešle notifikaci adminovi.
     */
    const clearStatus = async () => {
        if (!auth.currentUser) throw new Error('Not authenticated')
        const token = await auth.currentUser.getIdToken()
        const res = await fetch(`${API_URL}/api/users/me/clear-status`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
            const body = await res.json().catch(() => ({})) as { message?: string }
            throw new Error(body.message || 'Nepodařilo se zrušit stav účtu')
        }
        setAppUser((prev) => prev ? { ...prev, status: '' } : null)
    }

    /**
     * Odhlášení.
     */
    const logout = async () => {
        await signOut(auth)
    }

    return (
        <AuthContext.Provider value={{
            user: appUser,
            isAuthenticated: !!appUser,
            emailVerified,
            loading,
            login,
            loginWithGoogle,
            register,
            logout,
            initiateGoogleSignIn,
            completeRegistration,
            updateUserProfile,
            updateMarketingConsent,
            registerWithToken,
            registerWithTokenViaGoogle,
            sendVerificationEmail,
            reloadUser,
            clearStatus,
        }}>
            {!loading && children}
        </AuthContext.Provider>
    )
}
